import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { studentNav } from "@/config/nav";
import { StatCard, SectionTitle, StatusBadge } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, Cell,
} from "recharts";
import { BookOpen, Trophy, Clock, TrendingUp, ArrowRight, Award } from "lucide-react";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/student/dashboard").then((r) => setData(r.data)); }, []);

  const s = data?.stats || {};

  return (
    <DashboardLayout nav={studentNav} title="Overview">
      <div className="mb-8">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-1">Track your examinations and performance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8">
        <StatCard label="Available Exams" value={s.available_exams ?? "—"} icon={BookOpen} index={0} />
        <StatCard label="Average Score" value={`${s.average_score ?? 0}%`} icon={TrendingUp} accent="text-emerald-600" index={1} />
        <StatCard label="Completed" value={s.completed ?? "—"} icon={Trophy} index={2} sub={`${s.passed ?? 0} passed`} />
        <StatCard label="Under Evaluation" value={s.under_evaluation ?? "—"} icon={Clock} accent="text-amber-500" index={3} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-card border border-border p-6">
          <SectionTitle>Performance Trend</SectionTitle>
          {(data?.trend || []).length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-muted-foreground text-sm">Complete an exam to see your trend.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.trend}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip />
                <Line type="monotone" dataKey="percentage" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-card border border-border p-6">
          <SectionTitle>Subject Performance</SectionTitle>
          {(data?.subject_performance || []).length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.subject_performance} layout="vertical">
                <XAxis type="number" domain={[0, 100]} hide />
                <YAxis type="category" dataKey="subject" tick={{ fontSize: 11 }} width={80} axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey="average" radius={[0, 2, 2, 0]} fill="#002FA7" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-card border border-border p-6">
        <SectionTitle action={<Link to="/app/results" className="text-sm text-primary flex items-center gap-1">All results <ArrowRight className="h-3.5 w-3.5" /></Link>}>
          Recent Results
        </SectionTitle>
        {(data?.recent_results || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No published results yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {data.recent_results.map((r) => (
              <Link key={r.id} to={`/app/results/${r.id}`} className="flex items-center gap-4 py-3 hover:bg-secondary/40 -mx-2 px-2 transition-colors" data-testid={`result-row-${r.id}`}>
                <Award className={`h-5 w-5 ${r.passed ? "text-emerald-600" : "text-destructive"}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.exam_title}</div>
                  <div className="text-xs text-muted-foreground">{r.subject}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-semibold">{r.percentage}%</div>
                  <div className="text-xs">{r.passed ? <span className="text-emerald-600">PASS</span> : <span className="text-destructive">FAIL</span>}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
